import { DITypes } from "@/core/@types/types";
import { type HttpClient } from "@/core/services/protocols/types";
import { inject, injectable } from "inversify";
import {
  ICallPluggySyncronizationPayload,
  ICallPluggySyncronizationResponse,
  IContaResponseData,
  IPluggyOperationResponse,
  IValidatePluggyAccountResponse,
  PluggyGateway
} from "../ports/PluggyGateway";
import { type IToastNotification } from "@/core/composables/notification/types";
import { IContasResponseFin, IDefaultResponse } from "../types";
import {
  FinancasDevHttpDi,
  type IFinancasDevHttp
} from "@/core/services/FinancasDevHttp";

@injectable()
export default class PluggyGatewayAdapter implements PluggyGateway {
  @inject(DITypes.FINANCAS_HTTP_CLIENT)
  private httpClient!: HttpClient;

  @inject(DITypes.FINANCAS_API_HTTP_CLIENT)
  private httpClientFinancasApi!: HttpClient;

  @inject(FinancasDevHttpDi)
  private financasDevHttp!: IFinancasDevHttp;

  @inject(DITypes.NOTIFICATION)
  private notification!: IToastNotification<string>;

  async obterConnectToken(id?: string | null): Promise<string | undefined> {
    const response = await this.httpClientFinancasApi?.get<IDefaultResponse>({
      url: "v1/ContaAPI/GetPluggyConnectToken",
      queryParams: { itemId: id }
    });

    if (!response?.body?.Ok) return undefined;

    return response.body?.Result || undefined;
  }

  async validateExistingItemId(id: string): Promise<boolean> {
    const response = await this.httpClient?.get<IDefaultResponse>({
      url: "ContaAPI/ValidateExistingItemId",
      queryParams: { itemId: id }
    });

    return Boolean(response?.body?.Ok && response.body?.Result);
  }

  async validatePluggyAccount(webserviceId: string): Promise<boolean> {
    const response = await this.validatePluggyAccountResult(webserviceId);

    return response.Ok;
  }

  // Regra legada: preservar o erro para decidir se a reconexão exige confirmação.
  async validatePluggyAccountResult(
    webserviceId: string
  ): Promise<IValidatePluggyAccountResponse> {
    const response = await this.httpClient?.get<IDefaultResponse>({
      url: "WebserviceAPI/ValidatePluggyAccount",
      queryParams: { webserviceId }
    });

    const body = response?.body as
      | (IDefaultResponse & { Error?: string })
      | undefined;

    return {
      Ok: Boolean(body?.Ok),
      Error: body?.Error
    };
  }

  async obterConnectorId(id: string): Promise<number | undefined> {
    const response = await this.httpClientFinancasApi?.get<IDefaultResponse>({
      url: "v1/ContaAPI/GetPluggyBankId",
      queryParams: { bancoId: id }
    });

    if (!response?.body?.Ok) return undefined;

    const hasBancoId = response.body.Result;

    return hasBancoId ? Number(hasBancoId) : undefined;
  }

  async obterConta(id: string): Promise<IContasResponseFin | undefined> {
    const response = await this.httpClient?.get<IContaResponseData>({
      url: `/ContaAPI/Get/${id}`
    });

    if (!response?.body?.Ok) return;

    return response.body.Result;
  }

  async connectOpenFinanceByAccount(id: string): Promise<boolean> {
    const response = await this.httpClientFinancasApi?.post<IDefaultResponse>({
      url: "/v1/WebserviceAPI/ConnectOpenFinanceByAccount",
      body: {
        contaId: id
      }
    });

    if (!response?.body?.Ok) return false;

    return true;
  }

  async removeOnEventWebservicePluggy(
    id: string
  ): Promise<IPluggyOperationResponse> {
    // Permite exibir somente a mensagem retornada pelo backend no fluxo legado.
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.post(
        "/api/v1/WebserviceAPI/RemoveOnEventWebserviceByItemId",
        { itemId: id }
      );

      return {
        Ok: Boolean(response?.Ok),
        Error: response?.Error
      };
    }

    const response = await this.httpClientFinancasApi?.post<
      IDefaultResponse & { Error?: string }
    >({
      url: "/v1/WebserviceAPI/RemoveOnEventWebserviceByItemId",
      body: {
        itemId: id
      }
    });

    return {
      Ok: Boolean(response?.body?.Ok),
      Error: response?.body?.Error
    };
  }

  async callPluggySyncronization(
    params: ICallPluggySyncronizationPayload
  ): Promise<ICallPluggySyncronizationResponse> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.post(
        `/api/v1/WebserviceAPI/CallPluggySynchronization`,
        params
      );

      return {
        Ok: response.Ok || false,
        Error: response.Error,
        Result: response.Result || undefined
      };
    }

    const response =
      await this.httpClientFinancasApi?.post<ICallPluggySyncronizationResponse>({
        url: "/v1/WebserviceAPI/CallPluggySynchronization",
        body: params
      });

    return {
      Ok: Boolean(response?.body?.Ok),
      Error: response?.body?.Error,
      Result: response?.body?.Result || undefined
    };
  }
}
